/**
 * Identifies likely out-of-memory errors coming from WASM / the Rust core.
 *
 * Uses stack-trace pattern matching because the `RuntimeError: unreachable`
 * thrown by WASM on OOM is generic - we need the allocator frames to tell
 * an OOM apart from other panics. Also detects cascaded OOM where
 * `__wbindgen_malloc` becomes the first failing WASM frame after an initial
 * allocation failure.
 *
 * Kept dependency-free (no React/UI imports) so it can be shared by the error
 * screen, the window-level OOM handler, and `sendAnalyticsError`'s Sentry
 * severity classification.
 */
export const isOutOfMemoryError = (error: Error | unknown): boolean => {
  const errorString = error instanceof Error ? `${error.message}\n${error.stack ?? ''}` : String(error);
  const allocationPatterns = [
    'alloc::raw_vec::handle_error',
    'alloc::raw_vec::RawVec',
    'alloc::alloc::handle_alloc_error',
    'out of memory',
    'memory allocation',
  ];
  if (allocationPatterns.some((pattern) => errorString.includes(pattern))) {
    return true;
  }
  if (errorString.includes('unreachable')) {
    const lines = errorString.split('\n');
    const firstWasmFrame = lines.find((line) => line.includes('quadratic_core.wasm.'));
    if (firstWasmFrame?.includes('__wbindgen_malloc')) {
      return true;
    }
  }
  return false;
};
